// frontend/src/components/jobPlacement/JobPlacementList.jsx
import React, { useEffect, useState, useCallback } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const JobPlacementList = ({
  tenant_id,
  user_id,
  role,
  onEdit,
  onView,
  onRelatedForms,
}) => {
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState("updated_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [showArchived, setShowArchived] = useState(false);
  const apiBase = getApiBaseURL();

  const fetchPlans = useCallback(async () => {
    if (!tenant_id) return;

    setLoading(true);
    try {
      const response = await secureAxios.get(`${apiBase}/api/job-placements`, {
        params: {
          tenant_id,
          user_id,
          role,
          include_archived: showArchived ? "true" : "false",
        },
      });
      setPlans(response.data);
    } catch (err) {
      console.error("Error loading job placement plans:", err);
      toast.error("Failed to load job placement plans");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, user_id, role, apiBase, showArchived]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleLoadFullPlan = async (planId, callback) => {
    try {
      const response = await secureAxios.get(
        `${apiBase}/api/job-placements/${planId}`,
        { params: { tenant_id } }
      );
      callback(response.data);
    } catch (err) {
      console.error("Failed to load full plan data:", err);
      toast.error("Could not load full plan");
    }
  };

  const handleArchive = async (planId, consumerName) => {
    if (
      !window.confirm(
        `Are you sure you want to archive the plan for ${consumerName}?\n\nThe plan will be hidden from the list but can be restored later.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.put(
        `${apiBase}/api/job-placements/${planId}/archive`,
        {},
        {
          params: { tenant_id },
        }
      );
      toast.success("Plan archived successfully");
      fetchPlans();
    } catch (err) {
      console.error("Error archiving plan:", err);
      toast.error(err.response?.data?.error || "Failed to archive plan");
    }
  };

  const handleRestore = async (planId, consumerName) => {
    if (
      !window.confirm(
        `Restore the plan for ${consumerName}?\n\nThe plan will be visible in the active list again.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.put(
        `${apiBase}/api/job-placements/${planId}/restore`,
        {},
        {
          params: { tenant_id },
        }
      );
      toast.success("Plan restored successfully");
      fetchPlans();
    } catch (err) {
      console.error("Error restoring plan:", err);
      toast.error(err.response?.data?.error || "Failed to restore plan");
    }
  };

  const handleViewPdf = async (planId) => {
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/job-placements/pdf/${planId}`,
        { params: { tenant_id } }
      );
      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      } else {
        toast.error("PDF could not be generated");
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Unable to generate PDF");
    }
  };

  // Sort plans
  const sortedPlans = [...plans].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle date fields
    if (
      sortField === "date" ||
      sortField === "updated_at" ||
      sortField === "created_at"
    ) {
      aVal = new Date(aVal || 0);
      bVal = new Date(bVal || 0);
    }

    // Handle string fields (case-insensitive)
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Filter plans
  const filteredPlans = sortedPlans.filter((plan) =>
    plan.consumer_name?.toLowerCase().includes(filter.toLowerCase())
  );

  // Separate active and archived plans
  const activePlans = filteredPlans.filter((p) => !p.is_archived);
  const archivedPlans = filteredPlans.filter((p) => p.is_archived);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
    }
    return sortDirection === "asc" ? (
      <i className="bi bi-sort-up ms-1"></i>
    ) : (
      <i className="bi bi-sort-down ms-1"></i>
    );
  };

  const renderPlanRow = (plan, isArchived = false) => (
    <tr
      key={plan.job_placement_id}
      className={isArchived ? "table-secondary" : ""}
    >
      <td>
        <strong>{plan.consumer_name}</strong>
        {isArchived && (
          <span className="badge bg-secondary ms-2">Archived</span>
        )}
      </td>
      <td>{plan.date ? new Date(plan.date).toLocaleDateString() : "-"}</td>
      <td>{plan.created_by_name || "-"}</td>
      <td>
        {plan.updated_at ? new Date(plan.updated_at).toLocaleDateString() : "-"}
      </td>
      <td>
        <div className="btn-group btn-group-sm" role="group">
          <button
            className="btn btn-outline-info"
            onClick={() => handleLoadFullPlan(plan.job_placement_id, onView)}
            title="View Plan"
          >
            <i className="bi bi-eye"></i>
          </button>
          {!isArchived && (
            <>
              <button
                className="btn btn-outline-primary"
                onClick={() =>
                  handleLoadFullPlan(plan.job_placement_id, onEdit)
                }
                title="Edit Plan"
              >
                <i className="bi bi-pencil"></i>
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={() => handleViewPdf(plan.job_placement_id)}
                title="View PDF"
              >
                <i className="bi bi-file-pdf"></i>
              </button>
              <button
                className="btn btn-outline-success"
                onClick={() =>
                  handleLoadFullPlan(plan.job_placement_id, onRelatedForms)
                }
                title="CRP-31/32 Forms"
              >
                <i className="bi bi-files"></i>
              </button>
              <button
                className="btn btn-outline-warning"
                onClick={() =>
                  handleArchive(plan.job_placement_id, plan.consumer_name)
                }
                title="Archive Plan"
              >
                <i className="bi bi-archive"></i>
              </button>
            </>
          )}
          {isArchived && (
            <button
              className="btn btn-outline-success"
              onClick={() =>
                handleRestore(plan.job_placement_id, plan.consumer_name)
              }
              title="Restore Plan"
            >
              <i className="bi bi-arrow-counterclockwise"></i>
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  const renderMobileCard = (plan, isArchived = false) => (
    <li
      key={plan.job_placement_id}
      className={`list-group-item ${isArchived ? "bg-light" : ""}`}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="mb-1">
            {plan.consumer_name}
            {isArchived && (
              <span className="badge bg-secondary ms-2">Archived</span>
            )}
          </h6>
          <small className="text-muted">
            Plan Date:{" "}
            {plan.date ? new Date(plan.date).toLocaleDateString() : "-"}
          </small>
          <br />
          <small className="text-muted">
            Created By: {plan.created_by_name || "-"}
          </small>
          <br />
          <small className="text-muted">
            Updated:{" "}
            {plan.updated_at
              ? new Date(plan.updated_at).toLocaleDateString()
              : "-"}
          </small>
        </div>
      </div>
      <div className="mt-2 d-flex flex-wrap gap-1">
        <button
          className="btn btn-outline-info btn-sm"
          onClick={() => handleLoadFullPlan(plan.job_placement_id, onView)}
        >
          <i className="bi bi-eye me-1"></i>View
        </button>
        {!isArchived && (
          <>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleLoadFullPlan(plan.job_placement_id, onEdit)}
            >
              <i className="bi bi-pencil me-1"></i>Edit
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleViewPdf(plan.job_placement_id)}
            >
              <i className="bi bi-file-pdf me-1"></i>PDF
            </button>
            <button
              className="btn btn-outline-success btn-sm"
              onClick={() =>
                handleLoadFullPlan(plan.job_placement_id, onRelatedForms)
              }
            >
              <i className="bi bi-files me-1"></i>Forms
            </button>
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() =>
                handleArchive(plan.job_placement_id, plan.consumer_name)
              }
            >
              <i className="bi bi-archive me-1"></i>Archive
            </button>
          </>
        )}
        {isArchived && (
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() =>
              handleRestore(plan.job_placement_id, plan.consumer_name)
            }
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i>Restore
          </button>
        )}
      </div>
    </li>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading job placement plans...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Search/Filter and Show Archived Toggle */}
      <div className="mb-3">
        <div className="row g-2 align-items-center">
          <div className="col-md-8">
            <div className="input-group input-group-sm">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filter by consumer name..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => setFilter("")}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="showArchivedToggle"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showArchivedToggle">
                <i className="bi bi-archive me-1"></i>
                Show Archived ({archivedPlans.length})
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-muted small mb-2">
        Showing {activePlans.length} active plan
        {activePlans.length !== 1 ? "s" : ""}
        {showArchived && archivedPlans.length > 0 && (
          <span> + {archivedPlans.length} archived</span>
        )}
      </p>

      {filteredPlans.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          {plans.length === 0
            ? "No job placement plans found. Click 'New Plan' to create one."
            : "No plans match your search criteria."}
        </div>
      ) : (
        <>
          {/* Desktop/tablet view */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th
                    onClick={() => handleSort("consumer_name")}
                    style={{ cursor: "pointer" }}
                  >
                    Consumer
                    <SortIcon field="consumer_name" />
                  </th>
                  <th
                    onClick={() => handleSort("date")}
                    style={{ cursor: "pointer" }}
                  >
                    Plan Date
                    <SortIcon field="date" />
                  </th>
                  <th
                    onClick={() => handleSort("created_by_name")}
                    style={{ cursor: "pointer" }}
                  >
                    Created By
                    <SortIcon field="created_by_name" />
                  </th>
                  <th
                    onClick={() => handleSort("updated_at")}
                    style={{ cursor: "pointer" }}
                  >
                    Last Updated
                    <SortIcon field="updated_at" />
                  </th>
                  <th style={{ width: "240px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Active Plans */}
                {activePlans.map((plan) => renderPlanRow(plan, false))}

                {/* Archived Plans (if showing) */}
                {showArchived && archivedPlans.length > 0 && (
                  <>
                    <tr className="table-warning">
                      <td colSpan="5" className="text-center fw-bold">
                        <i className="bi bi-archive me-2"></i>
                        Archived Plans ({archivedPlans.length})
                      </td>
                    </tr>
                    {archivedPlans.map((plan) => renderPlanRow(plan, true))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile view */}
          <div className="d-block d-md-none">
            {/* Active Plans */}
            <ul className="list-group mb-3">
              {activePlans.map((plan) => renderMobileCard(plan, false))}
            </ul>

            {/* Archived Plans (if showing) */}
            {showArchived && archivedPlans.length > 0 && (
              <>
                <div className="alert alert-warning py-2 mb-2">
                  <i className="bi bi-archive me-2"></i>
                  <strong>Archived Plans ({archivedPlans.length})</strong>
                </div>
                <ul className="list-group">
                  {archivedPlans.map((plan) => renderMobileCard(plan, true))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default JobPlacementList;